import type { PromptTemplateChip } from '@/components/features/Editor';
import type { PromptAgent } from '@/types/prompts';

/**
 * A Prompt Template chip on its way from the Prompts page to an agent's
 * composer (VH3_CONNECT #593). Use prompt never sends: the page leaves the
 * filled chip here, keyed by agent and entity (the document or quote), then
 * opens that chat. The composer for that key takes it once and inserts it.
 *
 * sessionStorage, so it survives the route change but not the tab.
 */

const PREFIX = 'vh3.pendingPromptChip';

/** Fired on `window` when a chip is left, so an open composer takes it too. */
export const PENDING_PROMPT_CHIP_EVENT = 'vh3:pending-prompt-chip';

/** The key for an agent's composer: Connie has one, Tia and Salma one per entity. */
export const pendingPromptChipKey = (
  agent: PromptAgent,
  entityId?: string | number | null
) =>
  entityId === undefined || entityId === null || entityId === ''
    ? `${PREFIX}:${agent}`
    : `${PREFIX}:${agent}:${entityId}`;

const store = (): Storage | null => {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    return null;
  }
};

export const setPendingPromptChip = (key: string, chip: PromptTemplateChip) => {
  try {
    store()?.setItem(key, JSON.stringify(chip));
  } catch {
    // Storage full or blocked: the chat still opens, just without the chip.
  }
  window.dispatchEvent(
    new CustomEvent(PENDING_PROMPT_CHIP_EVENT, { detail: { key } })
  );
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);

/** A positive integer, from a number or a numeric string; else null. */
const positiveInt = (value: unknown) => {
  const number =
    typeof value === 'number'
      ? value
      : typeof value === 'string' && value.trim()
        ? Number(value)
        : NaN;
  return Number.isInteger(number) && number > 0 ? number : null;
};

/**
 * The stored chip in the shape the composer needs, or null. Storage may hold
 * a chip from an older build or one edited by hand, so nothing is trusted:
 * ids are numbers, `variables` an array of named entries, `values` strings.
 */
export const normalisePendingPromptChip = (
  raw: unknown
): PromptTemplateChip | null => {
  if (!isRecord(raw)) return null;
  const { title, body } = raw;
  const templateId = positiveInt(raw.templateId);
  const version = positiveInt(raw.version);
  if (typeof title !== 'string' || typeof body !== 'string') return null;
  if (templateId === null || version === null) return null;

  const variables = (Array.isArray(raw.variables) ? raw.variables : [])
    .filter(isRecord)
    .filter(item => typeof item.name === 'string' && item.name)
    .map(item => ({
      name: item.name as string,
      ...(typeof item.description === 'string' && {
        description: item.description,
      }),
      ...(typeof item.default === 'string' && { default: item.default }),
    }));
  const values = Object.fromEntries(
    Object.entries(isRecord(raw.values) ? raw.values : {}).filter(
      (entry): entry is [string, string] => typeof entry[1] === 'string'
    )
  );

  return {
    templateId,
    version,
    title,
    body,
    templateBody:
      typeof raw.templateBody === 'string' && raw.templateBody
        ? raw.templateBody
        : body,
    variables,
    values,
    edited: raw.edited === true,
  };
};

export const peekPendingPromptChip = (
  key: string
): PromptTemplateChip | null => {
  try {
    const raw = store()?.getItem(key);
    if (!raw) return null;
    return normalisePendingPromptChip(JSON.parse(raw));
  } catch {
    return null;
  }
};

export const hasPendingPromptChip = (key: string) =>
  peekPendingPromptChip(key) !== null;

export const clearPendingPromptChip = (key: string) => {
  try {
    store()?.removeItem(key);
  } catch {
    // Nothing to clear.
  }
};
